import { HttpClient, HttpResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { marker } from '@colsen1991/ngx-translate-extract-marker';
import {
    AsyncNotificationChange,
    BottomSheetService,
    HttpExtras,
    IFileResponse,
    MiscService,
    NotificationMessageLevel,
    NotificationService,
    ScanResult,
    UploadBottomSheetResult,
    UploadResult,
} from '@lib/core';
import { HttpStatus } from '@lib/util';
import { FileSaverService } from 'ngx-filesaver';
import { ReplaySubject } from 'rxjs';
import { IDeliveryReceipt } from './types/IDeliveryReceipt';
import { DeliveryReceiptDataService } from './delivery-receipt-data.service';

@Injectable({
    providedIn: 'root',
})
export class DeliveryReceiptFileService {
    readonly deliveryReceiptDataService = inject(DeliveryReceiptDataService);
    private readonly bottomSheet = inject(BottomSheetService);
    private readonly notificationService = inject(NotificationService);
    private readonly http = inject(HttpClient);
    private readonly fileSaverService = inject(FileSaverService);


    /**
     * Open a bottom sheet to attach a new file to this receipt
     * @param receipt - the receipt to use
     */
    public upload(receipt: IDeliveryReceipt): void {
        const ref = this.bottomSheet.upload({ uploadInternalPossible: false, multiple: true });
        ref.afterDismissed().subscribe((result: UploadBottomSheetResult) => {
            if (result) {
                const notification$: ReplaySubject<AsyncNotificationChange> = new ReplaySubject();
                this.notificationService.showNotification(
                    marker('Datei wird hochgeladen'),
                    NotificationMessageLevel.DEFAULT,
                    notification$,
                );
                if (result instanceof UploadResult) {
                    this.deliveryReceiptDataService.upload(receipt.id, result.formData, true).subscribe(
                        (files: IFileResponse[]) => {
                            receipt.files = files;
                            notification$.next(
                                new AsyncNotificationChange(
                                    marker('Datei wurde erfolgreich hochgeladen.'),
                                    NotificationMessageLevel.SUCCESS,
                                    true,
                                ),
                            );
                        },
                        () =>
                            notification$.next(
                                new AsyncNotificationChange(
                                    marker('Beim Hochladen der Datei ist leider ein Fehler aufgetreten.'),
                                    NotificationMessageLevel.ERROR,
                                    true,
                                ),
                            ),
                    );
                } else if (result instanceof ScanResult) {
                    this.deliveryReceiptDataService.uploadScannedImages(receipt.id, result.formData, true).subscribe(
                        (file: IFileResponse) => {
                            receipt.files.push(file);
                            notification$.next(
                                new AsyncNotificationChange(
                                    marker('Das gescannte Dokument wurde als PDF hochgeladen.'),
                                    NotificationMessageLevel.SUCCESS,
                                    true,
                                ),
                            );
                        },
                        () =>
                            notification$.next(
                                new AsyncNotificationChange(
                                    marker(
                                        'Beim Hochladen der gescannten Dokumente ist leider ein Fehler aufgetreten.',
                                    ),
                                    NotificationMessageLevel.ERROR,
                                    true,
                                ),
                            ),
                    );
                }
            }
        });
    }

    /**
     * Download a file that has been attached to this receipt
     * @param file - the file info to download
     */
    public downloadFile(file: IFileResponse): void {
        const errorMessage = marker('Fehler beim Herunterladen der Datei.');
        const params: HttpExtras = new HttpExtras({
            errorMessage: {
                [HttpStatus.NOT_FOUND]: marker('Die Datei konnte leider nicht gefunden werden.'),
                [HttpStatus.INTERNAL_SERVER_ERROR]: errorMessage,
                [HttpStatus.BAD_REQUEST]: errorMessage,
            },
        });
        this.http
            .get(file.fileLink, { responseType: 'blob', observe: 'response', params })
            .subscribe((res: HttpResponse<Blob>) => {
                this.fileSaverService.save(res.body, MiscService.parseContentDispositionFilename(res.headers));
            });
    }

    /**
     * Delete a file that is attached to this receipt
     * @param receiptId - the receipt id
     * @param fileId - the file id to delete
     * @param receipt - the receipt to use
     */
    public deleteFile(receiptId: number, fileId: string, receipt: IDeliveryReceipt): void {
        this.deliveryReceiptDataService.deleteFile(receiptId, fileId).subscribe((files: IFileResponse[]) => {
            const message = marker('Datei wurde gelöscht.');
            this.notificationService.showNotification(message, NotificationMessageLevel.SUCCESS);
            receipt.files = files;
        });
    }
}
